import { Close as CloseIcon } from "@mui/icons-material";
import {
  Container,
  Dialog,
  DialogContent,
  DialogTitle,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import IconButton from "@mui/material/IconButton";
import React, { useCallback } from "react";
import { styled } from "styled-components";
import { PADDING } from "ui/theme";

const SubContainer = styled.div`
  margin-bottom: ${PADDING.medium};
`;

interface EvChargerDetailsDialogProps {
  visible: boolean;
  onClose: () => void;
  GSTInclusive: boolean;
}

export const EvChargerDetailsDialog: React.FC<EvChargerDetailsDialogProps> = ({
  visible,
  onClose,
  GSTInclusive,
}) => {
  const handleCloseModal = useCallback(() => {
    onClose();
  }, [onClose]);

  const getAdjustedToExclusiveGSTPrice = useCallback(
    (value: string) => {
      if (!GSTInclusive) {
        return (parseFloat(value) / 1.15).toFixed(2);
      }
      return parseFloat(value).toFixed(2);
    },
    [GSTInclusive],
  );

  return (
    <Container>
      <Dialog
        onClose={handleCloseModal}
        aria-label="Learn more about the Evnex smart charger add-on."
        open={visible}
      >
        <DialogTitle>
          <>
            <Typography variant="h3">
              Learn more about the Evnex smart charger add-on.
            </Typography>
            {onClose && (
              <IconButton
                aria-label="close"
                onClick={onClose}
                sx={{
                  position: "absolute",
                  right: 8,
                  top: 8,
                }}
              >
                <CloseIcon />
              </IconButton>
            )}
          </>
        </DialogTitle>
        <DialogContent>
          <SubContainer>
            <Typography variant="caption">
              This plan comes with an option to add on a{" "}
              <b>Type 2 Evnex Smart Charger</b>. By adding on a smart charger
              here you are expressing interest in having a charger installed at
              your address. You will not be required to commit to purchasing a
              charger until you receive a personalised quote from Evnex
              (provided after you sign up for Z Power).
            </Typography>
          </SubContainer>
          <SubContainer>
            <TableContainer sx={{ marginBottom: `${PADDING.medium}` }}>
              <Table sx={{ minWidth: 400 }} aria-label="rates-table">
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <Typography variant="subtitle1" component="p">
                        Pricing
                      </Typography>
                    </TableCell>
                    <TableCell align="right" />
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow
                    sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                  >
                    <TableCell component="th" scope="row">
                      <Typography variant="body2" component="p">
                        Payment type options
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" component="p">
                        Fully installed cost
                      </Typography>
                    </TableCell>
                  </TableRow>
                  <TableRow
                    sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                  >
                    <TableCell component="th" scope="row">
                      <Typography variant="body2" component="p">
                        Upfront payment in full
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="caption" component="p">
                        From ${getAdjustedToExclusiveGSTPrice("2195")}*
                      </Typography>
                    </TableCell>
                  </TableRow>
                  <TableRow
                    sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                  >
                    <TableCell component="th" scope="row">
                      <Typography variant="body2" component="p">
                        24 month repayment interest free
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="caption" component="p">
                        From ${getAdjustedToExclusiveGSTPrice("91.46")} per
                        month*
                      </Typography>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell />
                    <TableCell component="th" scope="row" align="right">
                      <Typography variant="caption" component="p">
                        GST {GSTInclusive ? "inclusive" : "exclusive"}
                      </Typography>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
            <Typography variant="caption" display="inline">
              *Prices shown represent a standard install. If you have a complex
              charging layout, don't have enough room on your switch board, or
              require more than 3m of wiring between your distribution board and
              your charger then this cost will increase. You will be given the
              opportunity to review a quote for your specific situation before
              agreeing to purchase the charger.
            </Typography>
          </SubContainer>
          <SubContainer>
            <Typography variant="h6">Installation Requirements</Typography>
            <Typography variant="body2">
              Property type
              <br />
              <Typography variant="caption">
                Some property types may not be suitable for an Evnex EV Smart
                Charger, including apartments, rental properties, and some
                townhouses. Please be advised that if our installers find your
                property is not suitable for an Evnex EV Smart Charger add on
                you will still be signed up for our EV plan.
              </Typography>
            </Typography>
            <Typography variant="body2">
              Authority to install
              <br />
              <Typography variant="caption">
                In order to install a smart charger at a property, you must be
                the legal owner of that property, or have a signed statement
                from the owner of the property granting you authority to install
                a charger.
              </Typography>
            </Typography>
            <Typography variant="body2">
              Personal use
              <br />
              <Typography variant="caption">
                This offer is for charging privately owned vehicles. If you have
                a work electric vehicle, please email Z at
                workplacecharging@z.co.nz to find out more about work place
                charging.
              </Typography>
            </Typography>
            <Typography variant="caption">
              For more information see our{" "}
              <Link
                variant="caption"
                display="inline"
                href={`${process.env.REACT_APP_Z_WEBSITE_URL}power/tc-smart`}
                target="_blank"
                aria-label="Terms and Conditions"
              >
                Terms and Conditions
              </Link>
            </Typography>
          </SubContainer>
        </DialogContent>
      </Dialog>
    </Container>
  );
};
